/* eslint-disable max-lines */
import { saveCustomer } from 'actions';
import ToggleButton from 'components/ToggleButton';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getCustomer } from 'selectors/auth';
import { useSelector } from 'store';
import {
  CustomerConfigWithTeamsFields,
  formatCommaSeparatedList,
  isAnyO365ScrapeEnabled,
  normalizeNullableDate,
  NumberInputRow,
  parseCommaSeparatedList,
  setSubscriptionResourceEnabled,
  SettingRow,
  SettingsCardHeader,
  TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE,
  TEAMS_CHAT_SUBSCRIPTION_RESOURCE,
  DEFAULT_TEAMS_SUBSCRIPTION_RESOURCES,
  haveSameTeamsSubscriptionResources,
  TextInputRow,
  parsePositiveInt,
  parseNonNegativeInt,
} from './CustomerEditO365SettingsShared';

const CustomerEditO365TeamsSettings: React.FC = () => {
  const dispatch = useDispatch();
  const customer = useSelector(getCustomer);
  const config = customer?.config as CustomerConfigWithTeamsFields | undefined;

  const [enableTeamsChatScrape, setEnableTeamsChatScrape] = useState(false);
  const [enableTeamsChannelScrape, setEnableTeamsChannelScrape] = useState(false);
  const [enableSubscriptions, setEnableSubscriptions] = useState(false);
  const [enablePerUserChatSubscriptions, setEnablePerUserChatSubscriptions] = useState(true);
  const [enabledSubscriptionResources, setEnabledSubscriptionResources] = useState<string[]>(
    DEFAULT_TEAMS_SUBSCRIPTION_RESOURCES
  );
  const [teamsSubscriptionModel, setTeamsSubscriptionModel] = useState('B');
  const [teamsSubscriptionEvents, setTeamsSubscriptionEvents] = useState('created');
  const [teamsThreadContextDepth, setTeamsThreadContextDepth] = useState('5');
  const [teamsThreadContextWindowHours, setTeamsThreadContextWindowHours] = useState('8');
  const [fetchThreadContextOnMessage, setFetchThreadContextOnMessage] = useState(true);
  const [teamsAllowProcessAttachments, setTeamsAllowProcessAttachments] = useState(false);
  const [teamsSecurityGroupIds, setTeamsSecurityGroupIds] = useState('');
  const [teamsOnlyAllowUserIds, setTeamsOnlyAllowUserIds] = useState('');
  const [maxUserBatchSize, setMaxUserBatchSize] = useState('250');
  const [maxConcurrentTeamsScrape, setMaxConcurrentTeamsScrape] = useState('100');
  const [maxConcurrentChatsPerUserScrape, setMaxConcurrentChatsPerUserScrape] = useState('50');
  const [bigBackfillTeamsChatsScrapeEnabled, setBigBackfillTeamsChatsScrapeEnabled] =
    useState(false);
  const [
    bigBackfillTeamsChatsSubstrateScrapeEnabled,
    setBigBackfillTeamsChatsSubstrateScrapeEnabled,
  ] = useState(false);
  const [skipCacheTeamsChatsScrape, setSkipCacheTeamsChatsScrape] = useState(false);
  const [usePrimaryCredentialsForTeamsScrape, setUsePrimaryCredentialsForTeamsScrape] =
    useState(false);
  const [syncStartDate, setSyncStartDate] = useState('');
  const [syncEndDate, setSyncEndDate] = useState('');

  const resetFromCustomer = (): void => {
    if (!config) return;

    const teamsConfig = config.o365_config ?? {};
    setEnableTeamsChatScrape(teamsConfig.enable_teams_chat_scrape ?? false);
    setEnableTeamsChannelScrape(teamsConfig.enable_teams_channel_scrape ?? false);
    setEnableSubscriptions(teamsConfig.enable_subscriptions ?? false);
    setEnablePerUserChatSubscriptions(teamsConfig.enable_per_user_chat_subscriptions ?? true);
    setEnabledSubscriptionResources(
      teamsConfig.enabled_subscription_resources ?? DEFAULT_TEAMS_SUBSCRIPTION_RESOURCES
    );
    setTeamsSubscriptionModel(teamsConfig.teams_subscription_model ?? 'B');
    setTeamsSubscriptionEvents(teamsConfig.teams_subscription_events ?? 'created');
    setTeamsThreadContextDepth(String(teamsConfig.teams_thread_context_depth ?? 5));
    setTeamsThreadContextWindowHours(String(teamsConfig.teams_thread_context_window_hours ?? 8));
    setFetchThreadContextOnMessage(teamsConfig.fetch_thread_context_on_message ?? true);
    setTeamsAllowProcessAttachments(teamsConfig.teams_allow_process_attachments ?? false);
    setTeamsSecurityGroupIds(formatCommaSeparatedList(teamsConfig.teams_security_group_ids));
    setTeamsOnlyAllowUserIds(
      formatCommaSeparatedList(
        (teamsConfig.teams_only_allow_user_ids ?? []).map((id) => String(id))
      )
    );
    setMaxUserBatchSize(String(teamsConfig.max_user_batch_size ?? 250));
    setMaxConcurrentTeamsScrape(String(teamsConfig.max_concurrent_teams_scrape ?? 100));
    setMaxConcurrentChatsPerUserScrape(
      String(teamsConfig.max_concurrent_chats_per_user_scrape ?? 50)
    );
    setBigBackfillTeamsChatsScrapeEnabled(
      teamsConfig.big_backfill_teams_chats_scrape_enabled ?? false
    );
    setBigBackfillTeamsChatsSubstrateScrapeEnabled(
      teamsConfig.big_backfill_teams_chats_substrate_scrape_enabled ?? false
    );
    setSkipCacheTeamsChatsScrape(teamsConfig.skip_cache_teams_chats_scrape ?? false);
    setUsePrimaryCredentialsForTeamsScrape(
      teamsConfig.use_primary_credentials_for_teams_scrape ?? false
    );
    setSyncStartDate(config.sync_start_date ?? '');
    setSyncEndDate(config.sync_end_date ?? '');
  };

  useEffect(() => {
    if (!config) return;
    resetFromCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const parsedValues = (): {
    maxUserBatchSize: number;
    maxConcurrentTeamsScrape: number;
    maxConcurrentChatsPerUserScrape: number;
    teamsThreadContextDepth: number;
    teamsThreadContextWindowHours: number;
  } | null => {
    const batchSize = parsePositiveInt(maxUserBatchSize);
    const concurrentTeams = parsePositiveInt(maxConcurrentTeamsScrape);
    const concurrentChats = parsePositiveInt(maxConcurrentChatsPerUserScrape);
    const threadDepth = parseNonNegativeInt(teamsThreadContextDepth);
    const threadWindowHours = parseNonNegativeInt(teamsThreadContextWindowHours);

    if (
      batchSize === null ||
      concurrentTeams === null ||
      concurrentChats === null ||
      threadDepth === null ||
      threadWindowHours === null
    ) {
      return null;
    }

    return {
      maxUserBatchSize: batchSize,
      maxConcurrentTeamsScrape: concurrentTeams,
      maxConcurrentChatsPerUserScrape: concurrentChats,
      teamsThreadContextDepth: threadDepth,
      teamsThreadContextWindowHours: threadWindowHours,
    };
  };

  const isSubscriptionModelValid = (): boolean => {
    const model = teamsSubscriptionModel.trim().toUpperCase();
    return model === 'A' || model === 'B';
  };

  const isFormValid = (): boolean => parsedValues() !== null && isSubscriptionModelValid();

  // @ts-ignore
  const isOutlookScrapeEnabled = config?.o365_config?.enable_outlook_scrape ?? false;

  const syncSubscriptionsWithScrapes = (chatScrape: boolean, channelScrape: boolean): void => {
    setEnableSubscriptions(
      isAnyO365ScrapeEnabled({
        enableOutlookScrape: isOutlookScrapeEnabled,
        enableTeamsChatScrape: chatScrape,
        enableTeamsChannelScrape: channelScrape,
      })
    );
  };

  const handleTeamsChatScrapeToggle = (): void => {
    setEnableTeamsChatScrape((value) => {
      const next = !value;
      syncSubscriptionsWithScrapes(next, enableTeamsChannelScrape);
      return next;
    });
  };

  const handleTeamsChannelScrapeToggle = (): void => {
    setEnableTeamsChannelScrape((value) => {
      const next = !value;
      syncSubscriptionsWithScrapes(enableTeamsChatScrape, next);
      return next;
    });
  };

  const handleChatSubscriptionResourceToggle = (): void => {
    setEnabledSubscriptionResources((resources) =>
      setSubscriptionResourceEnabled(
        resources,
        TEAMS_CHAT_SUBSCRIPTION_RESOURCE,
        !resources.includes(TEAMS_CHAT_SUBSCRIPTION_RESOURCE)
      )
    );
  };

  const handleChannelSubscriptionResourceToggle = (): void => {
    setEnabledSubscriptionResources((resources) =>
      setSubscriptionResourceEnabled(
        resources,
        TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE,
        !resources.includes(TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE)
      )
    );
  };

  const handleSave = (): void => {
    if (!config) return;

    const values = parsedValues();
    if (!values || !isSubscriptionModelValid()) {
      return;
    }

    const nonTeamsResources = (config.o365_config?.enabled_subscription_resources ?? []).filter(
      (resource) =>
        resource !== TEAMS_CHAT_SUBSCRIPTION_RESOURCE &&
        resource !== TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE
    );
    const teamsResources = enabledSubscriptionResources.filter(
      (resource) =>
        resource === TEAMS_CHAT_SUBSCRIPTION_RESOURCE ||
        resource === TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE
    );

    dispatch(
      saveCustomer({
        config: {
          ...config,
          sync_start_date: normalizeNullableDate(syncStartDate),
          sync_end_date: normalizeNullableDate(syncEndDate),
          o365_config: {
            ...(config.o365_config ?? {}),
            // @ts-ignore
            enable_teams_chat_scrape: enableTeamsChatScrape,
            enable_teams_channel_scrape: enableTeamsChannelScrape,
            enable_subscriptions: enableSubscriptions,
            enable_per_user_chat_subscriptions: enablePerUserChatSubscriptions,
            enabled_subscription_resources: [...nonTeamsResources, ...teamsResources],
            teams_subscription_model: teamsSubscriptionModel.trim().toUpperCase(),
            teams_subscription_events: teamsSubscriptionEvents.trim() || 'created',
            teams_thread_context_depth: values.teamsThreadContextDepth,
            teams_thread_context_window_hours: values.teamsThreadContextWindowHours,
            fetch_thread_context_on_message: fetchThreadContextOnMessage,
            teams_allow_process_attachments: teamsAllowProcessAttachments,
            teams_security_group_ids: parseCommaSeparatedList(teamsSecurityGroupIds),
            teams_only_allow_user_ids: parseCommaSeparatedList(teamsOnlyAllowUserIds),
            max_user_batch_size: values.maxUserBatchSize,
            max_concurrent_teams_scrape: values.maxConcurrentTeamsScrape,
            max_concurrent_chats_per_user_scrape: values.maxConcurrentChatsPerUserScrape,
            big_backfill_teams_chats_scrape_enabled: bigBackfillTeamsChatsScrapeEnabled,
            big_backfill_teams_chats_substrate_scrape_enabled:
              bigBackfillTeamsChatsSubstrateScrapeEnabled,
            skip_cache_teams_chats_scrape: skipCacheTeamsChatsScrape,
            use_primary_credentials_for_teams_scrape: usePrimaryCredentialsForTeamsScrape,
          },
        },
      })
    );
  };

  const hasChanges = (): boolean => {
    if (!config) return false;

    const teamsConfig = config.o365_config ?? {};
    const values = parsedValues();
    if (!values) return true;

    if ((teamsConfig.enable_teams_chat_scrape ?? false) !== enableTeamsChatScrape) return true;
    if ((teamsConfig.enable_teams_channel_scrape ?? false) !== enableTeamsChannelScrape) {
      return true;
    }
    if ((teamsConfig.enable_subscriptions ?? false) !== enableSubscriptions) return true;
    if (
      (teamsConfig.enable_per_user_chat_subscriptions ?? true) !== enablePerUserChatSubscriptions
    ) {
      return true;
    }
    if (
      !haveSameTeamsSubscriptionResources(
        enabledSubscriptionResources,
        teamsConfig.enabled_subscription_resources ?? DEFAULT_TEAMS_SUBSCRIPTION_RESOURCES
      )
    ) {
      return true;
    }
    if (
      (teamsConfig.teams_subscription_model ?? 'B').toUpperCase() !==
      teamsSubscriptionModel.trim().toUpperCase()
    ) {
      return true;
    }
    if ((teamsConfig.teams_subscription_events ?? 'created') !== teamsSubscriptionEvents.trim()) {
      return true;
    }
    if ((teamsConfig.teams_thread_context_depth ?? 5) !== values.teamsThreadContextDepth) {
      return true;
    }
    if (
      (teamsConfig.teams_thread_context_window_hours ?? 8) !== values.teamsThreadContextWindowHours
    ) {
      return true;
    }
    if ((teamsConfig.fetch_thread_context_on_message ?? true) !== fetchThreadContextOnMessage) {
      return true;
    }
    if ((teamsConfig.teams_allow_process_attachments ?? false) !== teamsAllowProcessAttachments) {
      return true;
    }
    if (
      JSON.stringify(parseCommaSeparatedList(teamsSecurityGroupIds)) !==
      JSON.stringify(teamsConfig.teams_security_group_ids ?? [])
    ) {
      return true;
    }
    if (
      JSON.stringify(parseCommaSeparatedList(teamsOnlyAllowUserIds)) !==
      JSON.stringify((teamsConfig.teams_only_allow_user_ids ?? []).map((id) => String(id)))
    ) {
      return true;
    }
    if ((teamsConfig.max_user_batch_size ?? 250) !== values.maxUserBatchSize) return true;
    if ((teamsConfig.max_concurrent_teams_scrape ?? 100) !== values.maxConcurrentTeamsScrape) {
      return true;
    }
    if (
      (teamsConfig.max_concurrent_chats_per_user_scrape ?? 50) !==
      values.maxConcurrentChatsPerUserScrape
    ) {
      return true;
    }
    if (
      (teamsConfig.big_backfill_teams_chats_scrape_enabled ?? false) !==
      bigBackfillTeamsChatsScrapeEnabled
    ) {
      return true;
    }
    if (
      (teamsConfig.big_backfill_teams_chats_substrate_scrape_enabled ?? false) !==
      bigBackfillTeamsChatsSubstrateScrapeEnabled
    ) {
      return true;
    }
    if ((teamsConfig.skip_cache_teams_chats_scrape ?? false) !== skipCacheTeamsChatsScrape) {
      return true;
    }
    if (
      (teamsConfig.use_primary_credentials_for_teams_scrape ?? false) !==
      usePrimaryCredentialsForTeamsScrape
    ) {
      return true;
    }
    if ((config.sync_start_date ?? '') !== syncStartDate) return true;
    if ((config.sync_end_date ?? '') !== syncEndDate) return true;

    return false;
  };

  if (!config) {
    return null;
  }

  return (
    <div className="mt-6 mb-8 bg-white border border-litlingo-gray-2 rounded">
      <SettingsCardHeader
        title="Teams settings"
        description=""
        saveTestId="o365-teams-save-button"
        hasChanges={hasChanges()}
        isValid={isFormValid()}
        onCancel={resetFromCustomer}
        onSave={handleSave}
      />

      {/* <SyncDatePickerRow
        label="Sync start date"
        description="Earliest date for Teams insight sync. Shared with Outlook. Leave unset for no limit."
        value={syncStartDate}
        onChange={setSyncStartDate}
        testId="teams-sync-start-date-input"
      />

      <SyncDatePickerRow
        label="Sync end date"
        description="Latest date for Teams insight sync. Shared with Outlook. Leave unset for no limit."
        value={syncEndDate}
        onChange={setSyncEndDate}
        testId="teams-sync-end-date-input"
      /> */}

      <SettingRow
        label="Enable Teams chat scrape"
        description="Turn on scheduled Microsoft Teams chat scraping for this customer."
        control={
          <ToggleButton
            dataTestid="enable-teams-chat-scrape-toggle"
            isOn={enableTeamsChatScrape}
            handleToggleClick={handleTeamsChatScrapeToggle}
          />
        }
      />

      <SettingRow
        label="Enable Teams channel scrape"
        description="Turn on scheduled Microsoft Teams channel scraping for this customer."
        control={
          <ToggleButton
            dataTestid="enable-teams-channel-scrape-toggle"
            isOn={enableTeamsChannelScrape}
            handleToggleClick={handleTeamsChannelScrapeToggle}
          />
        }
      />

      <SettingRow
        label="Enable subscriptions"
        description="Switch for Graph webhook subscriptions. Shared with Outlook."
        control={
          <ToggleButton
            dataTestid="teams-enable-subscriptions-toggle"
            isOn={enableSubscriptions}
            handleToggleClick={(): void => setEnableSubscriptions((value) => !value)}
            disabled={
              !isAnyO365ScrapeEnabled({
                enableOutlookScrape: isOutlookScrapeEnabled,
                enableTeamsChatScrape,
                enableTeamsChannelScrape,
              }) && !enableSubscriptions
            }
          />
        }
      />

      <SettingRow
        label="Enable per-user chat subscriptions"
        description="Create per user Teams chat subscriptions when Prevent chat is active."
        control={
          <ToggleButton
            dataTestid="enable-per-user-chat-subscriptions-toggle"
            isOn={enablePerUserChatSubscriptions}
            handleToggleClick={(): void => setEnablePerUserChatSubscriptions((value) => !value)}
          />
        }
      />

      <SettingRow
        label="Subscribe to chat"
        description="Include the chats Graph resource."
        control={
          <ToggleButton
            dataTestid="teams-subscription-resource-chat-toggle"
            isOn={enabledSubscriptionResources.includes(TEAMS_CHAT_SUBSCRIPTION_RESOURCE)}
            handleToggleClick={handleChatSubscriptionResourceToggle}
          />
        }
      />

      <SettingRow
        label="Subscribe to channel"
        description="Include the teams Graph resource"
        control={
          <ToggleButton
            dataTestid="teams-subscription-resource-channel-toggle"
            isOn={enabledSubscriptionResources.includes(TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE)}
            handleToggleClick={handleChannelSubscriptionResourceToggle}
          />
        }
      />

      <div className="px-5 py-4 border-t border-litlingo-gray-2">
        <label className="block" htmlFor="teams-subscription-model">
          <p className="text text--medium">Teams subscription model</p>
          <p className="text-xs mb-2">
            Graph Teams subscription licensing model. Accepted values are A or B.
          </p>
          <select
            id="teams-subscription-model"
            data-testid="teams-subscription-model-select"
            value={teamsSubscriptionModel.trim().toUpperCase() === 'A' ? 'A' : 'B'}
            onChange={(event): void => setTeamsSubscriptionModel(event.target.value)}
            className="form-select block w-32 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          >
            <option value="A">A</option>
            <option value="B">B</option>
          </select>
        </label>
      </div>

      <TextInputRow
        id="teams-subscription-events"
        label="Teams subscription events"
        description="Graph changeType filter for chats/teams subscriptions (e.g. created)."
        value={teamsSubscriptionEvents}
        onChange={setTeamsSubscriptionEvents}
        testId="teams-subscription-events-input"
        placeholder="created"
      />

      <NumberInputRow
        id="teams-thread-context-depth"
        label="Teams thread context depth"
        description="Number of surrounding messages to fetch for Teams thread context."
        value={teamsThreadContextDepth}
        onChange={setTeamsThreadContextDepth}
        testId="teams-thread-context-depth-input"
        min={0}
      />

      <NumberInputRow
        id="teams-thread-context-window-hours"
        label="Teams thread context window hours"
        description="Hours before and after sent_at used for Teams thread context filtering."
        value={teamsThreadContextWindowHours}
        onChange={setTeamsThreadContextWindowHours}
        testId="teams-thread-context-window-hours-input"
        min={0}
      />

      <SettingRow
        label="Fetch thread context on message"
        description="Fetch Teams thread context when processing incoming messages."
        control={
          <ToggleButton
            dataTestid="fetch-thread-context-on-message-toggle"
            isOn={fetchThreadContextOnMessage}
            handleToggleClick={(): void => setFetchThreadContextOnMessage((value) => !value)}
          />
        }
      />

      <SettingRow
        label="Process Teams attachments"
        description="Process attachments on Teams messages during scrape and ingestion."
        control={
          <ToggleButton
            dataTestid="teams-allow-process-attachments-toggle"
            isOn={teamsAllowProcessAttachments}
            handleToggleClick={(): void => setTeamsAllowProcessAttachments((value) => !value)}
          />
        }
      />

      <TextInputRow
        id="teams-security-group-ids"
        label="Teams security group IDs"
        description="Comma-separated Azure AD group IDs used to populate Teams allow listed users."
        value={teamsSecurityGroupIds}
        onChange={setTeamsSecurityGroupIds}
        testId="teams-security-group-ids-input"
      />

      <TextInputRow
        id="teams-only-allow-user-ids"
        label="Teams only allow user IDs"
        description="Comma-separated Graph user IDs allowed for Teams processing."
        value={teamsOnlyAllowUserIds}
        onChange={setTeamsOnlyAllowUserIds}
        testId="teams-only-allow-user-ids-input"
      />

      <NumberInputRow
        id="teams-max-user-batch-size"
        label="Max user batch size"
        description="Maximum number of users processed per Teams scrape batch. Shared with Outlook."
        value={maxUserBatchSize}
        onChange={setMaxUserBatchSize}
        testId="teams-max-user-batch-size-input"
      />

      <NumberInputRow
        id="max-concurrent-teams-scrape"
        label="Max concurrent Teams scrape"
        description="Maximum number of concurrent Teams scrape workers."
        value={maxConcurrentTeamsScrape}
        onChange={setMaxConcurrentTeamsScrape}
        testId="max-concurrent-teams-scrape-input"
      />

      <NumberInputRow
        id="max-concurrent-chats-per-user-scrape"
        label="Max concurrent chats per user scrape"
        description="Maximum concurrent chats processed per user during Teams scrape."
        value={maxConcurrentChatsPerUserScrape}
        onChange={setMaxConcurrentChatsPerUserScrape}
        testId="max-concurrent-chats-per-user-scrape-input"
      />

      <SettingRow
        label="Big backfill Teams chats scrape"
        description="Run large historical backfill jobs for Teams chats."
        control={
          <ToggleButton
            dataTestid="big-backfill-teams-chats-scrape-toggle"
            isOn={bigBackfillTeamsChatsScrapeEnabled}
            handleToggleClick={(): void => setBigBackfillTeamsChatsScrapeEnabled((value) => !value)}
          />
        }
      />

      <SettingRow
        label="Big backfill Teams chats substrate scrape"
        description="Enable substrate based historical backfill for Teams chats."
        control={
          <ToggleButton
            dataTestid="big-backfill-teams-chats-substrate-scrape-toggle"
            isOn={bigBackfillTeamsChatsSubstrateScrapeEnabled}
            handleToggleClick={(): void =>
              setBigBackfillTeamsChatsSubstrateScrapeEnabled((value) => !value)
            }
          />
        }
      />

      <SettingRow
        label="Skip cache Teams chats scrape"
        description="Skip Redis cache during Teams chat scrape."
        control={
          <ToggleButton
            dataTestid="skip-cache-teams-chats-scrape-toggle"
            isOn={skipCacheTeamsChatsScrape}
            handleToggleClick={(): void => setSkipCacheTeamsChatsScrape((value) => !value)}
          />
        }
      />

      <SettingRow
        label="Use primary credentials for Teams scrape"
        description="Use the customer app registration credentials instead of Teams bot credentials."
        control={
          <ToggleButton
            dataTestid="use-primary-credentials-for-teams-scrape-toggle"
            isOn={usePrimaryCredentialsForTeamsScrape}
            handleToggleClick={(): void =>
              setUsePrimaryCredentialsForTeamsScrape((value) => !value)
            }
          />
        }
      />
    </div>
  );
};

export default CustomerEditO365TeamsSettings;
